import React, { useState, useEffect, useMemo } from 'react'
import { AntModal, AntInput, AntSelect, RichEditor } from "components/AntComponents";
import { Button } from "antd";
import { Formik } from 'formik';
import {
    currentStatus,
    cityList,
    worldCities,
    countryList,
    investorStatus,
    dealType,
} from "formFields";
import Attachments from "components/AdminComponents/Attachments";
import { startupCrudFormSchema } from "./validations";
import Api from "Api/Api";
import Utils from "Utils";

export function StartupCrud({ visible, onClose, dynamicFields, data, onSubmit }) {
    const INITIAL_FORM_DATA = {
        industry: null,
        stage: null,
        sector: [],
        segment: null,
        name: null,
        business_description: null,
        founder_first_name: null,
        founder_last_name: null,
        founder_email: null,
        active: null,
        mobile_number: null,
        valuation: null,
        website: null,
        current_status: null,
        ask_amount: null,
        deal_type: null,
        country: null,
        city: null,
        lead_source: null,
        comment_box: null,
        relationship_manager: null,
        tags: []
    }
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);

    useEffect(() => {
        if (data) {
            setFormData(data);
        }
    }, [data]);

    useEffect(() => {
        setIsOpen(visible)
    }, [visible]);

    const closeModal = () => {
        setFormData(INITIAL_FORM_DATA);
        onClose();
        setIsOpen(false);
    }

    const addStartup = async (record) => {
        const response = await Api.createStartup(record);
        if (response.status !== 200) {
            Utils.notify(
                Utils.errorMessageGenerator(response.data),
                "warning",
                4000
            );
        } else {
            Utils.notify("Startup created successfully!", "success", 4000);
            return response.data;
        }
    };

    const startupUpdated = async (id, payload) => {
        const response = await Api.updateStartupData(id, payload);
        if (response.status === 400) {
            Utils.notify(
                Utils.errorMessageGenerator(response.data),
                "warning",
                4000
            );
        } else if (response.status === 200) {
            Utils.notify("Startup updated successfully!", "success", 4000);
            return response.data;
        }
    };

    const handleSubmit = async (payload, { setSubmitting }) => {
        let newData = payload;
        setSubmitting(true);
        let created = false;
        if (data) {
            await startupUpdated(data.id, payload);
        } else {
            created = true;
            newData = await addStartup(payload);
        }
        onSubmit(newData, created);
        setSubmitting(false);
        closeModal();
    }

    return (
        <AntModal
            title={`${data ? "Edit" : "Create"} Startup`}
            isOpen={isOpen}
            onClose={closeModal}
            options={{
                width: "40%"
            }}
        >
            <Formik
                initialValues={formData}
                enableReinitialize
                validationSchema={startupCrudFormSchema}
                onSubmit={handleSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleSubmit,
                    isSubmitting,
                    setFieldValue,
                }) => {
                    const cityOptions = values.country === "India" ? cityList : (worldCities[values.country] || []);
                    return (
                    <form onSubmit={handleSubmit}>
                        <h4>Startup basic details</h4>
                        <hr />
                        <div className="grid grid-cols-2 gap-10 mb-2">
                            <AntInput
                                value={values.name}
                                setFieldValue={setFieldValue}
                                fieldName={"name"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntInput
                                value={values.founder_first_name}
                                setFieldValue={setFieldValue}
                                fieldName={"founder_first_name"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntInput
                                value={values.founder_last_name}
                                setFieldValue={setFieldValue}
                                fieldName={"founder_last_name"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntInput
                                value={values.founder_email}
                                setFieldValue={setFieldValue}
                                fieldName={"founder_email"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntInput
                                value={values.mobile_number}
                                setFieldValue={setFieldValue}
                                fieldName={"mobile_number"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntSelect
                                value={values.country}
                                dataSource={countryList}
                                setFieldValue={setFieldValue}
                                fieldName="country"
                                errors={errors}
                                touched={touched}
                                options={{
                                    showSearch: true
                                }}
                                onChange={() => setFieldValue('city', null)}
                                isRequired
                            />
                            <AntSelect
                                value={values.city}
                                dataSource={cityOptions}
                                setFieldValue={setFieldValue}
                                fieldName="city"
                                errors={errors}
                                touched={touched}
                                options={{
                                    showSearch: true
                                }}
                            />
                            <div>
                                <Attachments />
                            </div>

                        </div>
                        <RichEditor
                            value={values.business_description}
                            fieldName={"business_description"}
                            setFieldValue={setFieldValue}
                            errors={errors}
                            touched={touched}
                            isRequired
                        />
                        <h4 className="mt-3">Type of Startup</h4>
                        <hr />
                        <div className="grid grid-cols-2 gap-10 mb-2">
                            <AntInput
                                value={values.valuation}
                                setFieldValue={setFieldValue}
                                fieldName={"valuation"}
                                errors={errors}
                                touched={touched}
                            />
                            <AntSelect
                                value={values.industry}
                                dataSource={dynamicFields.industry_choices || []}
                                setFieldValue={setFieldValue}
                                fieldName="industry"
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntSelect
                                value={values.stage}
                                dataSource={dynamicFields.preferred_stage_choices || []}
                                setFieldValue={setFieldValue}
                                fieldName="stage"
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntSelect
                                value={values.segment}
                                dataSource={dynamicFields.client_segment_choices || []}
                                setFieldValue={setFieldValue}
                                fieldName="segment"
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntSelect
                                value={values.sector}
                                dataSource={dynamicFields.sector_choices || []}
                                setFieldValue={setFieldValue}
                                fieldName="sector"
                                errors={errors}
                                touched={touched}
                                options={{
                                    mode: 'multiple'
                                }}
                                isRequired
                            />
                            <AntSelect
                                value={values.current_status}
                                dataSource={currentStatus}
                                setFieldValue={setFieldValue}
                                fieldName="current_status"
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntSelect
                                value={values.deal_type}
                                dataSource={dealType}
                                setFieldValue={setFieldValue}
                                fieldName="deal_type"
                                errors={errors}
                                touched={touched}
                            />
                            <AntSelect
                                value={values.active}
                                dataSource={investorStatus}
                                setFieldValue={setFieldValue}
                                fieldName="active"
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntInput
                                value={values.ask_amount}
                                setFieldValue={setFieldValue}
                                fieldName={"ask_amount"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntInput
                                value={values.lead_source}
                                setFieldValue={setFieldValue}
                                fieldName={"lead_source"}
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntSelect
                                value={values.relationship_manager}
                                dataSource={dynamicFields.relationship_managers_choices}
                                setFieldValue={setFieldValue}
                                fieldName="relationship_manager"
                                errors={errors}
                                touched={touched}
                                isRequired
                            />
                            <AntInput
                                value={values.website}
                                setFieldValue={setFieldValue}
                                fieldName={"website"}
                                errors={errors}
                                touched={touched}
                            />
                            <AntInput
                                value={values.comment_box}
                                setFieldValue={setFieldValue}
                                fieldName={"comment_box"}
                                errors={errors}
                                touched={touched}
                            />
                            <AntSelect
                                value={values.tags}
                                dataSource={dynamicFields.tag_choices}
                                setFieldValue={setFieldValue}
                                fieldName="tags"
                                errors={errors}
                                touched={touched}
                                options={{
                                    mode: 'multiple',
                                }}
                            />
                        </div>
                        <Button type="primary" htmlType="submit" className="flex flex-row-reverse">Save</Button>

                    </form>
                    );
                }}
            </Formik>
        </AntModal>
    )
}
